<template>
  <div v-if="open">
    <div class="auth-backdrop" @click="close"></div>
    <div class="auth-layer">
      <div class="auth-card" role="dialog" aria-modal="true" :aria-label="title">
        <div class="auth-head">
          <h3 class="auth-title">{{ title }}</h3>
          <button class="auth-x" @click="close" aria-label="Close">×</button>
        </div>

        <div class="auth-tabs">
          <button class="auth-tab" :class="{active: mode==='signin'}" @click="switchMode('signin')">Sign in</button>
          <button class="auth-tab" :class="{active: mode==='signup'}" @click="switchMode('signup')">Sign up</button>
          <button class="auth-tab" :class="{active: mode==='reset'}"  @click="switchMode('reset')">Reset</button>
        </div>

        <form class="auth-body" @submit.prevent="onSubmit">
          <template v-if="mode==='signup'">
            <div class="auth-field">
              <label>Email</label>
              <input v-model="email" type="email" required autocomplete="email" class="auth-input" />
            </div>
            <div class="auth-field">
              <label>Username</label>
              <input v-model="username" type="text" minlength="3" maxlength="24" required class="auth-input" />
            </div>
            <div class="auth-field">
              <label>Display name (optional)</label>
              <input v-model="displayName" type="text" maxlength="40" class="auth-input" />
            </div>
            <div class="auth-field">
              <label>Password</label>
              <input v-model="password" type="password" required autocomplete="new-password" class="auth-input" />
            </div>
          </template>

          <template v-else-if="mode==='signin'">
            <div class="auth-field">
              <label>Email</label>
              <input v-model="email" type="email" required autocomplete="email" class="auth-input" />
            </div>
            <div class="auth-field">
              <label>Password</label>
              <input v-model="password" type="password" required autocomplete="current-password" class="auth-input" />
            </div>
            <div class="auth-row">
              <button type="button" class="auth-link" @click="switchMode('reset')">Forgot Password?</button>
            </div>
          </template>

          <template v-else>
            <div class="auth-field">
              <label>Email</label>
              <input v-model="email" type="email" required autocomplete="email" class="auth-input" />
            </div>
            <p class="auth-hint">We’ll email you a reset link.</p>
          </template>

          <p v-if="error" class="auth-msg error">{{ error }}</p>
          <p v-if="message" class="auth-msg ok">{{ message }}</p>

          <button type="submit" class="auth-btn" :disabled="busy">
            <span v-if="!busy">{{ cta }}</span>
            <span v-else class="auth-pulse">Working…</span>
          </button>

          <div class="auth-alt" v-if="mode==='signin'">
            Don’t have an account?
            <button type="button" class="auth-link" @click="switchMode('signup')">Sign Up</button>
          </div>
          <div class="auth-alt" v-if="mode==='signup'">
            Already have an account?
            <button type="button" class="auth-link" @click="switchMode('signin')">Sign In</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { auth as fbAuth } from '@/firebase/firebase'
import { signInWithEmailAndPassword } from 'firebase/auth'


const props = defineProps({ open: Boolean, title: { type: String, default: "Welcome" } });
const emit = defineEmits(["close", "signed-in"]);

const mode = ref("signin");
const email = ref("");
const password = ref("");
const username = ref("");
const displayName = ref("");
const busy = ref(false);
const error = ref("");
const message = ref("");

const auth = useAuthStore();

watch(() => props.open, (o) => { if (o) reset(); });
watch(
  () => auth.isSignedIn,
  (v) => { if (v) emit('signed-in') }
)
function reset() {
  mode.value = "signin";
  email.value = password.value = username.value = displayName.value = "";
  busy.value = false; error.value = ""; message.value = "";
}

function switchMode(m) { mode.value = m; error.value = ""; message.value = ""; }

const cta = computed(() =>
  mode.value === "signup" ? "Create Account" :
  mode.value === "signin" ? "Sign In" : "Send Reset Link"
);

function toNiceError(e) {
  const code = e?.code || "";
  if (code.includes("email-already-in-use")) return "That email is already in use.";
  if (code.includes("invalid-email")) return "Please enter a valid email.";
  if (code.includes("weak-password")) return "Password is too weak.";
  if (code.includes("invalid-credential")) return "Invalid email or password.";
  if (code.includes("operation-not-allowed")) return "Email/Password sign-in is disabled.";
  return e?.message || "Something went wrong.";
}

async function onSubmit() {
  error.value = ""; message.value = ""; busy.value = true;
  try {
    if (mode.value === "signup") {
      await auth.signUp({ email: email.value, password: password.value, displayName: displayName.value, username: username.value });
      emit("signed-in"); emit("close");
    } else if (mode.value === "signin") {
   // Sign in directly with Firebase Auth (store will update via onAuthStateChanged)
   await signInWithEmailAndPassword(fbAuth, email.value, password.value)
   emit("signed-in"); emit("close");
 } else {
      await auth.sendReset(email.value);
      message.value = "If that email exists, a reset link has been sent.";
    }
  } catch (e) {
    error.value = toNiceError(e);
  } finally {
    busy.value = false;
  }
}

function close() { if (!busy.value) emit("close"); }
</script>

<style scoped>
/* Backdrop + layer */
.auth-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.45);backdrop-filter:blur(2px);z-index:9998;}
.auth-layer{position:fixed;inset:0;display:grid;place-items:center;z-index:9999;padding:16px;}
/* Card */
.auth-card{width:100%;max-width:420px;background:#fff;border-radius:16px;box-shadow:0 24px 60px rgba(0,0,0,.25);padding:20px 20px 18px;}
.auth-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.auth-title{font-size:20px;font-weight:700;margin:0;}
.auth-x{border:0;background:transparent;font-size:24px;line-height:1;border-radius:8px;width:36px;height:36px;cursor:pointer;}
.auth-x:hover{background:#f1f4f7;}
/* Tabs */
.auth-tabs{display:flex;gap:8px;margin:12px 0 16px;}
.auth-tab{padding:8px 12px;border-radius:999px;border:1px solid #e6e9ef;background:#f6f7f9;font-size:14px;cursor:pointer;}
.auth-tab.active{background:#2563eb;color:#fff;border-color:#2563eb;}
/* Form */
.auth-body{display:grid;gap:12px;}
.auth-field{display:grid;gap:6px;}
.auth-field > label{font-size:13px;color:#5b667a;}
.auth-input{height:42px;border-radius:12px;border:1px solid #d4dae4;padding:0 12px;font-size:14px;outline:0;transition:border .15s, box-shadow .15s;}
.auth-input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.15);}
.auth-hint{font-size:13px;color:#5b667a;margin:0;}
.auth-row{display:flex;justify-content:flex-end;}
/* Messages */
.auth-msg{border-radius:12px;padding:10px 12px;font-size:13px;}
.auth-msg.ok{background:#ecfdf5;color:#065f46;}
.auth-msg.error{background:#fef2f2;color:#b91c1c;}
/* Buttons/Links */
.auth-btn{height:46px;border:0;border-radius:12px;background:#2563eb;color:#fff;font-weight:700;font-size:15px;cursor:pointer;}
.auth-btn:hover{background:#1e4fcc;}
.auth-btn:disabled{opacity:.7;cursor:not-allowed;}
.auth-pulse{animation:pulse 1s ease-in-out infinite;}
@keyframes pulse{0%{opacity:.8}50%{opacity:.4}100%{opacity:.8}}
.auth-alt{font-size:13px;text-align:center;color:#5b667a;}
.auth-link{border:0;background:none;color:#2563eb;font-weight:600;cursor:pointer;padding:0 2px;}
.auth-link:hover{text-decoration:underline;}
</style>
